import { Event } from '@nostr/tools/wasm'
import { npubEncode } from '@nostr/tools/nip19'
import type { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { getEventHint } from '@/services/client.service'
import { getNoteBech32Id } from './event'
import { tagNameEquals } from './tag'

export type ReferencedCoords = {
  kind: number
  pubkey: string
  identifier: string
}

/**
 * Address coordinates an event refers to, parsed from its first `a` tag
 * shaped like `<kind>:<owner-hex-pubkey>:<identifier>`. Used so external
 * clients can be deep-linked as `<owner>/<identifier>/...` and similar.
 */
export function getReferencedCoordsFromEvent(event: Event): ReferencedCoords | undefined {
  for (const tag of event.tags) {
    if (tag[0] !== 'a' || !tag[1]) continue
    const [kindStr, pubkey, ...rest] = tag[1].split(':')
    if (!kindStr || !pubkey || rest.length === 0) continue
    if (!/^\d+$/.test(kindStr)) continue
    const identifier = rest.join(':')
    if (!identifier) continue
    return { kind: parseInt(kindStr), pubkey, identifier }
  }
  return undefined
}

/**
 * Resolve a client handler urlPattern for an event. Supported placeholders:
 * `{}` (nip19 naddr/nevent), `{e}`/`{id}` (hex id), `{kind}` (own kind),
 * `{hint}` (relay hint), `{p}`/`{pubkey}` (hex pubkey of the referenced
 * address from the event's `a` tag, falls back to event author),
 * `{npub}` (that pubkey as npub), `{d}` (identifier of the referenced
 * address from the event's `a` tag, falls back to own `d` tag).
 */
export function resolveClientUrlPattern(pattern: string, event: Event): string {
  const ref = getReferencedCoordsFromEvent(event)
  // addressable reference if the event points at one, otherwise the event itself
  const p = ref?.pubkey ?? event.pubkey
  const d = ref?.identifier ?? event.tags.find(tagNameEquals('d'))?.[1] ?? ''

  return resolveClientUrlPatternWithValues(pattern, {
    code: getNoteBech32Id(event),
    id: event.id,
    pubkey: p,
    kind: String(event.kind),
    d,
    hint: getEventHint(event.id, event)
  })
}

export function resolveClientUrlPatternFromPointer(
  pattern: string,
  pointer: EventPointer | AddressPointer,
  code: string
): string {
  return resolveClientUrlPatternWithValues(pattern, {
    code,
    id: 'id' in pointer ? pointer.id : '',
    pubkey: 'id' in pointer ? (pointer.author ?? '') : pointer.pubkey,
    kind: pointer.kind?.toString() ?? '',
    d: 'identifier' in pointer ? pointer.identifier : '',
    hint: pointer.relays?.[0] ?? ''
  })
}

function resolveClientUrlPatternWithValues(
  pattern: string,
  values: { code: string; id: string; pubkey: string; kind: string; d: string; hint: string }
): string {
  let npub = ''
  try {
    if (values.pubkey) npub = npubEncode(values.pubkey)
  } catch {
    /***/
  }

  return pattern
    .replace(/\{\}/g, values.code)
    .replace(/\{e\}/g, values.id)
    .replace(/\{id\}/g, values.id)
    .replace(/\{p\}/g, values.pubkey)
    .replace(/\{pubkey\}/g, values.pubkey)
    .replace(/\{npub\}/g, npub)
    .replace(/\{kind\}/g, values.kind)
    .replace(/\{d\}/g, values.d)
    .replace(/\{hint\}/g, values.hint)
}
